/**
 * Home Safety Audit — Entry Page
 * Route: /home-audit
 *
 * Render paths:
 *   1. Loading                        -> skeleton
 *   2. IN_PROGRESS session            -> Navigate to firstIncompleteRoomSlug
 *   3. No active session + completed  -> Options screen (view previous | start new)
 *   4. No active session + none prior -> Stairs question + Start CTA
 */

import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/utils/cn';
import {
  useActiveSession,
  useCreateSession,
  useLatestCompletedSession,
} from '../hooks/useHomeAudit';
import { activeRoomOrder, firstIncompleteRoomSlug } from '../utils/roomMap';
import { getScoreLabel } from '../utils/scoreColor';

type StairsChoice = 'has-stairs' | 'no-stairs' | null;

export const AuditEntryPage = () => {
  const navigate = useNavigate();
  const { data: activeSession, isLoading: sessionLoading } = useActiveSession();
  const { data: latestCompleted, isLoading: latestLoading } = useLatestCompletedSession();
  const createSession = useCreateSession();
  const [stairsChoice, setStairsChoice] = useState<StairsChoice>(null);
  const [showNewAudit, setShowNewAudit] = useState(false);

  if (sessionLoading || latestLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-11 w-48" />
        </div>
      </div>
    );
  }

  // Resume branch — redirect to first incomplete room
  if (activeSession && activeSession.status === 'IN_PROGRESS') {
    const target = firstIncompleteRoomSlug(activeSession);
    if (target === null) return <Navigate to="/home-audit/results" replace />;
    return <Navigate to={`/home-audit/${target}`} replace />;
  }

  const handleStart = async () => {
    if (stairsChoice === null) return;
    const stairsNa = stairsChoice === 'no-stairs';
    const session = await createSession.mutateAsync(stairsNa);
    const firstRoom = activeRoomOrder(stairsNa)[0];
    navigate(`/home-audit/${firstRoom}`);
    void session;
  };

  const canStart = stairsChoice !== null && !createSession.isPending;

  // Options screen — previous completed session exists and user hasn't chosen to start fresh
  if (latestCompleted && !showNewAudit) {
    const scoreLabel = getScoreLabel(latestCompleted.compositeScore);
    const completedDate = latestCompleted.completedAt
      ? format(new Date(latestCompleted.completedAt), 'MMMM d, yyyy')
      : 'Unknown date';

    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
          <h1 className="text-3xl font-medium">Home Safety Audit</h1>

          <Card className="p-6">
            <CardContent className="p-0 space-y-4">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wide">
                  Previous Audit
                </p>
                <p className="text-base font-medium mt-1">
                  {latestCompleted.compositeScore !== null
                    ? `${latestCompleted.compositeScore.toFixed(1)}/10 — ${scoreLabel}`
                    : scoreLabel}
                </p>
                <p className="text-sm text-muted-foreground">Completed {completedDate}</p>
              </div>
              <Button
                className="bg-[#026857] hover:bg-[#025246] text-white h-11"
                onClick={() =>
                  navigate(`/home-audit/results?sessionId=${latestCompleted.id}`)
                }
                data-testid="view-previous-results"
              >
                View Previous Results
              </Button>
            </CardContent>
          </Card>

          <div className="flex items-center gap-4">
            <div className="flex-1 border-t border-border" />
            <span className="text-sm text-muted-foreground">or</span>
            <div className="flex-1 border-t border-border" />
          </div>

          <Button
            type="button"
            variant="outline"
            className="h-11 w-full"
            onClick={() => setShowNewAudit(true)}
            data-testid="start-new-audit"
          >
            Start a New Audit
          </Button>
        </div>
      </div>
    );
  }

  // Fresh start — stairs question + CTA
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
        <h1 className="text-3xl font-medium">Home Safety Audit</h1>
        <p className="text-muted-foreground">
          A guided, room-by-room safety assessment. Answer a short set of questions for each
          area of your home and receive a prioritized action plan with cost estimates.
        </p>

        <Card className="p-6">
          <CardContent className="p-0 space-y-4">
            <p className="text-base font-medium">Does your home have stairs?</p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className={cn(
                  'h-11 flex-1 sm:flex-none sm:min-w-[140px]',
                  stairsChoice === 'has-stairs' &&
                    'bg-[#026857]/10 border-[#026857] text-[#026857] font-medium',
                )}
                onClick={() => setStairsChoice('has-stairs')}
                data-testid="stairs-yes"
              >
                Yes, it does
              </Button>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  'h-11 flex-1 sm:flex-none sm:min-w-[140px]',
                  stairsChoice === 'no-stairs' &&
                    'bg-[#026857]/10 border-[#026857] text-[#026857] font-medium',
                )}
                onClick={() => setStairsChoice('no-stairs')}
                data-testid="stairs-no"
              >
                No stairs
              </Button>
            </div>
          </CardContent>
        </Card>

        <Button
          type="button"
          className="bg-[#026857] hover:bg-[#025246] text-white h-11"
          disabled={!canStart}
          onClick={handleStart}
          data-testid="start-audit"
        >
          {createSession.isPending ? 'Starting\u2026' : 'Start Home Safety Audit'}
        </Button>

        {createSession.isError && (
          <p className="text-sm text-red-600">
            We couldn&apos;t start your audit. Please refresh the page and try again.
          </p>
        )}
      </div>
    </div>
  );
};
